import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/api';

const OTP = ({ email }) => {
  const [otp, setOtp] = useState(Array(6).fill(''));
  const inputRefs = useRef(Array(6).fill(null));
  const navigate = useNavigate();
  const [authError, setAuthError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Resend: the SERVER generates and stores a new OTP.
  const otpSender = () => {
    api.post("/users/sendotp", { email })
      .then((response) => {
        setSuccessMessage(response.data?.message || 'OTP sent successfully');
      })
      .catch((error) => {
        setAuthError(error.response?.data?.message || 'Failed to send OTP');
        setOtp(Array(6).fill(''));
      });
  };

  const handleChange = (index, value) => {
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value !== '' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && index > 0 && otp[index] === '') {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);

    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setOtp(newOtp);
      inputRefs.current[Math.min(pastedData.length, 5)]?.focus();
    }
  };

  // The OTP is verified on the SERVER; only a correct code returns a session.
  const handleVerify = () => {
    setAuthError('');
    setIsVerifying(true);
    api.post('/users/verifyOtp', { email, otp: otp.join('') }, { withCredentials: true })
      .then((response) => {
        if (response.status === 200) {
          setSuccessMessage('User login successful');
          setTimeout(() => navigate("/vehicles"), 1000);
        }
      })
      .catch((error) => {
        console.log(error);
        setAuthError(error.response?.data?.message || 'Invalid OTP');
        setOtp(Array(6).fill(''));
      })
      .finally(() => setIsVerifying(false));
  };

  const resendOtp = () => {
    setAuthError('');
    setSuccessMessage('');
    setOtp(Array(6).fill(''));
    otpSender();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#eff1f3] px-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-4 sm:px-8 pt-6 pb-4 text-center">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">Enter OTP</h2>
          <p className="mt-2 text-xs sm:text-sm text-gray-600">
            Please enter the verification code sent to your email
          </p>
        </div>

        <div className="px-4 sm:px-8 pb-6">
          <div className="flex justify-center space-x-2 sm:space-x-4 mb-6 sm:mb-8">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-8 h-10 sm:w-12 sm:h-12 text-center text-lg sm:text-2xl font-semibold border rounded-lg 
                          border-[#a9b9e3] text-gray-900
                          focus:border-[#6f48e5] focus:ring-2 focus:ring-[#6f48e5] focus:outline-none
                          transition-all duration-200"
              />
            ))}
          </div>

          {authError && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{authError}</p>
                </div>
              </div>
            </div>
          )}
                    
          {successMessage && (
            <div className="rounded-md bg-green-50 p-4 border border-green-200 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-10.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414L9 13.414l5.707-5.707z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{successMessage}</p>
                </div>
              </div>
            </div>
          )}
          
          <button
            type="button"
            disabled={!otp.every(digit => digit !== '') || isVerifying}
            className="w-full py-2 px-4 bg-[#6f48e5] hover:bg-[#5a3ac4] text-white
                      text-sm font-medium rounded-lg transition-colors duration-200
                      disabled:bg-[#a9b9e3] disabled:cursor-not-allowed
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6f48e5]"
            onClick={handleVerify}
          >
            {isVerifying ? 'Verifying...' : 'Verify OTP'}
          </button>

          <div className="mt-4 text-center">
            <button
              className="text-xs sm:text-sm text-gray-600 hover:text-[#6f48e5] transition-colors duration-200"
              onClick={resendOtp}
            >
              Resend OTP
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTP;